import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { Modal } from "react-bootstrap";
import { ChevronLeft, EyeOff, Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Chamado } from "../../types/types";
import Loading from "../../components/Loading";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";
import styles from "./Chamados.module.css";

const formatDate = (dateStr?: string | Date | null): string => {
  if (!dateStr) return "N/A";
  return new Date(dateStr)
    .toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", " às");
};

const STATUS_ORDER: Record<string, number> = {
  Pendente: 1,
  Respondido: 2,
  Encerrado: 3,
  Desabilitado: 4,
};

const sortChamados = (lista: Chamado[]): Chamado[] => {
  return [...lista].sort((a, b) => {
    const statusA = STATUS_ORDER[a.status] ?? 4;
    const statusB = STATUS_ORDER[b.status] ?? 4;

    if (statusA !== statusB) return statusA - statusB;

    return (
      new Date(b.horariO_CRIADO).getTime() -
      new Date(a.horariO_CRIADO).getTime()
    );
  });
};

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  Pendente: { label: "Pendente", className: styles.badgePendente },
  Respondido: { label: "Respondido", className: styles.badgeRespondido },
  Encerrado: { label: "Encerrado", className: styles.badgeEncerrado },
  Desabilitado: { label: "Desabilitado", className: styles.badgeDesabilitado },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config = STATUS_MAP[status];
  if (!config) return null;

  return (
    <div className={`${styles.badge} ${config.className}`}>
      <span className={styles.dot} />
      <span>{config.label}</span>
    </div>
  );
};

const Chamados = () => {
  const [chamados, setChamados] = useState<Chamado[] | string>([]);
  const [modalShow, setModalShow] = useState(false);
  const [modalRelatoriosShow, setModalRelatoriosShow] = useState(false);
  const [modalUserShow, setModalUserShow] = useState(false);
  const [resposta, setResposta] = useState("");
  const [chamadoAtual, setChamadoAtual] = useState<Chamado | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const token = useSelector((state: RootState) => state.auth.token);
  const usuario = useSelector((state: RootState) => state.auth.user);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useSessionExpirationCheck(sessionExpiration, token);

  const isAdmin = usuario?.nivel === "1";

  const handleLoadChamados = useCallback(async () => {
    if (!usuario) return;

    try {
      const url = !isAdmin
        ? `${process.env.REACT_APP_API}/GetAllByUserId?id=${usuario.id}`
        : `${process.env.REACT_APP_API}/api/chamado`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      // 1. Tratamos os erros HTTP antes de ler o corpo da resposta
      if (!response.ok) {
        // Se for 404, provavelmente é o caso de "Nenhum chamado encontrado"
        if (response.status === 404) {
          setChamados([]); // Define como lista vazia para sumir com a mensagem de erro da tela
          return;
        }

        // Para outros erros, tentamos ler como texto primeiro para evitar que o JSON quebre
        const errorText = await response.text();
        console.error("Erro da API:", errorText);
        throw new Error("Não foi possível carregar os chamados.");
      }

      // 2. Se a resposta está OK (200), lemos o JSON com segurança
      const data = await response.json();
      setChamados(data);
    } catch (error) {
      if (error instanceof Error) {
        console.error("Erro ao buscar chamados:", error.message);
        setChamados("Houve um erro ao buscar chamados");
      }
    }
  }, [token, usuario, isAdmin]);

  useEffect(() => {
    if (!token) {
      navigate("/area-do-cliente");
      return;
    }

    handleLoadChamados();
    document.title = "Íkone | Chamados";
  }, [token, navigate, handleLoadChamados, sessionExpiration, dispatch]);

  const sortedChamados = useMemo(() => {
    return Array.isArray(chamados) ? sortChamados(chamados) : [];
  }, [chamados]);

  const [popoverAberto, setPopoverAberto] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setPopoverAberto(null);
      }
    };

    if (popoverAberto) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [popoverAberto]);

  const handleResponderChamado = async () => {
    if (chamadoAtual?.status === "Encerrado") {
      toast.error("Não é possível responder um chamado encerrado.");
      return;
    }
    if (!resposta.trim()) {
      toast.error("A resposta não pode estar vazia.");
      return;
    }

    try {
      const corpoRequisicao = {
        ID_CHAMADO: chamadoAtual?.uuid,
        TEXTO_RESPOSTA: resposta,
        DATA_RESPOSTA: new Date().toISOString(),
        ID_USUARIO_RESPOSTA: usuario?.id,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API}/api/Chamado/${chamadoAtual?.uuid}/responder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(corpoRequisicao),
        },
      );

      if (response.ok) {
        toast.success("Resposta enviada com sucesso.");
        const novaRespostaCriada = await response.json();
        setResposta("");

        setChamados((prev) =>
          Array.isArray(prev)
            ? prev.map((c) =>
                chamadoAtual && c.uuid === chamadoAtual.uuid
                  ? {
                      ...c,
                      status: "Respondido",
                      respostas: [...c.respostas, novaRespostaCriada],
                    }
                  : c,
              )
            : prev,
        );

        setChamadoAtual((prev) =>
          prev
            ? {
                ...prev,
                status: "Respondido",
                respostas: [...prev.respostas, novaRespostaCriada],
              }
            : null,
        );
      } else {
        const errorData = await response.json();
        console.error(errorData.errors?.[0]);
        toast.error("Erro ao enviar resposta.");
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error("Erro ao responder chamado:", error.message);
      }
    }
  };

  const handleApagarChamado = useCallback(
    async (uuid: string) => {
      const confirmacao = window.confirm(
        "Tem certeza de que deseja apagar este chamado? Essa ação não pode ser desfeita.",
      );
      if (!confirmacao) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API}/api/chamado/${uuid}`,
          { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.ok) {
          toast.success("Chamado apagado com sucesso.");
          setChamados((prev) =>
            Array.isArray(prev) ? prev.filter((c) => c.uuid !== uuid) : prev,
          );
        } else {
          toast.error("Erro ao apagar o chamado.");
        }
      } catch (error) {
        if (error instanceof Error) console.error(error.message);
      } finally {
        setPopoverAberto(null);
      }
    },
    [token],
  );

  const handleDesabilitarChamado = useCallback(
    async (uuid: string) => {
      const confirmacao = window.confirm(
        "Tem certeza de que deseja desabilitar este chamado?",
      );
      if (!confirmacao) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API}/api/chamado/desabilitar/${uuid}`,
          { method: "PUT", headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.ok) {
          toast.success("Chamado desabilitado com sucesso.");
          setChamados((prev) =>
            Array.isArray(prev)
              ? prev.map((c) =>
                  c.uuid === uuid ? { ...c, status: "Desabilitado" } : c,
                )
              : prev,
          );
        } else {
          toast.error("Erro ao desabilitar o chamado.");
        }
      } catch (error) {
        if (error instanceof Error) console.error(error.message);
      } finally {
        setPopoverAberto(null);
      }
    },
    [token],
  );

  const handleEncerrarChamado = useCallback(async () => {
    if (!chamadoAtual) return;

    if (chamadoAtual.status === "Encerrado") {
      toast.error("O chamado já foi encerrado.");
      return;
    }

    const confirmacao = window.confirm(
      "Tem certeza de que deseja encerrar o chamado?",
    );
    if (!confirmacao) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/api/chamado/encerrar/${chamadoAtual.uuid}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        toast.success("Chamado encerrado com sucesso.");
        setChamadoAtual((prev) =>
          prev ? { ...prev, status: "Encerrado" } : prev,
        );
        setChamados((prev) =>
          Array.isArray(prev)
            ? prev.map((c) =>
                c.uuid === chamadoAtual.uuid
                  ? { ...c, status: "Encerrado" }
                  : c,
              )
            : prev,
        );
      } else {
        toast.error("Erro ao encerrar o chamado.");
      }
    } catch (error) {
      if (error instanceof Error) console.error(error.message);
    }
  }, [chamadoAtual, token]);

  const handleDownload = async (caminho: string, nomeArquivo: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/api/chamado/DownloadRelatorio?caminho=${encodeURIComponent(
          caminho,
        )}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!response.ok) throw new Error("Erro ao baixar o arquivo");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nomeArquivo;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof Error) toast.error(error.message);
    }
  };

  const handleUploadChamadoRelatorio = async (
    chamado: Chamado,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      toast.error("Nenhum arquivo selecionado.");
      return;
    }

    const formData = new FormData();
    formData.append("chamado_id", chamado.uuid);
    formData.append("enviador", String(usuario?.login));
    formData.append("relatorio", file);

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API}/relatorio`, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Relatório enviado com sucesso.");
      } else {
        toast.error("Erro ao enviar relatório.");
      }
    } catch (error) {
      if (error instanceof Error) console.error("Erro:", error.message);
    } finally {
      event.target.value = "";
      setIsLoading(false);
    }
  };

  const NovoChamadoCard = () => (
    <div
      className={styles.newCard}
      role="button"
      tabIndex={0}
      onClick={() => (window.location.href = "/chamado")}
    >
      <Plus size={22} />
      <span>Novo Chamado</span>
    </div>
  );

  const renderErrorState = () => (
    <p className={styles.errorText}>{chamados as string}</p>
  );

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <button
            className={styles.backButton}
            onClick={() => navigate(-1)}
            aria-label="Voltar"
          >
            <ChevronLeft size={22} />
          </button>
          <h2 className={styles.title}>Meus Chamados</h2>
        </header>

        {!Array.isArray(chamados) ? (
          renderErrorState()
        ) : (
          <div className={styles.grid}>
            {sortedChamados.map((chamado) => (
              <div key={chamado.uuid} className={styles.card}>
                <div className={styles.cardTop}>
                  <div className={styles.cardInfo}>
                    <span className={styles.infoLabel}>Chamado</span>
                    <span className={styles.infoValue}>
                      {chamado.num_Chamado ? `#${chamado.num_Chamado}` : "N/A"}
                    </span>

                    <span className={styles.infoLabel}>Criado em</span>
                    <span className={styles.infoValue}>
                      {formatDate(chamado.horariO_CRIADO)}
                    </span>
                  </div>

                  <StatusBadge status={chamado.status} />
                </div>

                <button
                  className={styles.btnPrimary}
                  onClick={() => {
                    setModalUserShow(true);
                    setChamadoAtual(chamado);
                  }}
                >
                  Acessar
                </button>
              </div>
            ))}

            <NovoChamadoCard />
          </div>
        )}

        {/* Modal - detalhes do chamado (cliente) */}
        <Modal
          show={modalUserShow}
          onHide={() => setModalUserShow(false)}
          centered
          size="lg"
          contentClassName={styles.modalContent}
        >
          <Modal.Header closeButton className={styles.modalHeader}>
            <Modal.Title className={styles.modalTitle}>
              <strong>Informações do Chamado</strong>
              <div className={styles.modalSubtitle}>
                <span>
                  <strong>Chamado:</strong>{" "}
                  {chamadoAtual?.num_Chamado
                    ? `#${chamadoAtual.num_Chamado}`
                    : "Não Informado"}
                </span>
                <span>
                  <strong>Criado em:</strong>{" "}
                  {formatDate(chamadoAtual?.horariO_CRIADO)}
                </span>
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className={styles.modalBody}>
            <div className={styles.detailsGrid}>
              <div>
                <span className={styles.infoLabel}>Empresa</span>
                <p className={styles.infoText}>{chamadoAtual?.empresa}</p>
              </div>
              <div>
                <span className={styles.infoLabel}>Email</span>
                <p className={styles.infoText}>{chamadoAtual?.email}</p>
              </div>
              <div>
                <span className={styles.infoLabel}>Telefone</span>
                <p className={styles.infoText}>{chamadoAtual?.telefone}</p>
              </div>
              <div>
                <span className={styles.infoLabel}>Solicitação</span>
                <p className={styles.infoText}>
                  {chamadoAtual?.tipoSolicitacao}
                </p>
              </div>
            </div>

            <div className={styles.fullWidthBlock}>
              <span className={styles.infoLabel}>Descrição</span>
              <p className={styles.infoText}>{chamadoAtual?.descricao}</p>
            </div>

            <div className={styles.fullWidthBlock}>
              <span className={styles.infoLabel}>Respostas</span>

              {chamadoAtual?.respostas && chamadoAtual.respostas.length > 0 ? (
                <div className={styles.responseHistory}>
                  {chamadoAtual.respostas.map((resp) => (
                    <div key={resp.id} className={styles.responseItem}>
                      <p className={styles.responseText}>
                        {resp.textO_RESPOSTA}
                      </p>
                      <small className={styles.responseMeta}>
                        Respondido por: {resp.nomE_USUARIO_RESPOSTA} em{" "}
                        {formatDate(resp.datA_RESPOSTA)}
                      </small>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.errorText}>Nenhuma resposta encontrada.</p>
              )}

              {chamadoAtual?.status !== "Encerrado" &&
                chamadoAtual?.respostas &&
                chamadoAtual.respostas.length > 0 && (
                  <div className={styles.fullWidthBlock}>
                    <label htmlFor="user-resposta" className={styles.infoLabel}>
                      Sua Resposta
                    </label>
                    <textarea
                      id="user-resposta"
                      className={styles.textarea}
                      value={resposta}
                      onChange={(e) => setResposta(e.target.value)}
                      rows={4}
                      placeholder="Digite sua resposta aqui..."
                    />
                  </div>
                )}
            </div>
          </Modal.Body>

          <Modal.Footer className={styles.modalFooter}>
            <button
              className={styles.btnOutline}
              onClick={() => setModalUserShow(false)}
            >
              Fechar
            </button>
            {chamadoAtual?.status !== "Encerrado" &&
              chamadoAtual?.respostas &&
              chamadoAtual.respostas.length > 0 && (
                <button
                  className={styles.btnPrimary}
                  onClick={handleResponderChamado}
                >
                  Devolver Resposta
                </button>
              )}
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  /* ------------------------- Render: Admin ------------------------- */

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          className={styles.backButton}
          onClick={() => navigate(-1)}
          aria-label="Voltar"
        >
          <ChevronLeft size={22} />
        </button>
        <h2 className={styles.title}>Lista de Chamados</h2>
      </header>

      {!Array.isArray(chamados) ? (
        renderErrorState()
      ) : (
        <div className={styles.grid}>
          {sortedChamados.map((chamado) => (
            <div key={chamado.uuid} className={styles.card}>
              <div className={styles.cardTop}>
                <div className={styles.cardInfo}>
                  <span className={styles.infoLabel}>Chamado</span>
                  <span className={styles.infoValue}>
                    {chamado.num_Chamado ? `#${chamado.num_Chamado}` : "N/A"}
                  </span>

                  <span className={styles.infoLabel}>Criado em</span>
                  <span className={styles.infoValue}>
                    {formatDate(chamado.horariO_CRIADO)}
                  </span>
                </div>

                <div className={styles.cardActions}>
                  <StatusBadge status={chamado.status} />

                  <div className={styles.actionWrapper}>
                    <button
                      className={styles.btnIconDanger}
                      aria-label="Mais ações"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPopoverAberto((prev) =>
                          prev === chamado.uuid ? null : chamado.uuid,
                        );
                      }}
                    >
                      <X size={16} />
                    </button>

                    {popoverAberto === chamado.uuid && (
                      <div className={styles.popover} ref={popoverRef}>
                        <p className={styles.popoverTitle}>
                          O que deseja fazer?
                        </p>

                        <button
                          className={styles.popoverBtnDanger}
                          onClick={() => handleApagarChamado(chamado.uuid)}
                        >
                          <Trash2 size={14} /> Apagar
                        </button>

                        <button
                          className={styles.popoverBtnOutline}
                          onClick={() => handleDesabilitarChamado(chamado.uuid)}
                        >
                          <EyeOff size={14} /> Desabilitar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <hr className={styles.divider} />

              <div className={styles.cardBody}>
                <p>
                  <strong>Empresa:</strong> {chamado.empresa}
                </p>
                <p>
                  <strong>Email:</strong> {chamado.email}
                </p>
                <p>
                  <strong>Telefone:</strong> {chamado.telefone}
                </p>
                <p>
                  <strong>Solicitação:</strong> {chamado.tipoSolicitacao}
                </p>
                <p className={styles.truncate}>
                  <strong>Descrição:</strong> {chamado.descricao}
                </p>
              </div>

              <button
                className={styles.btnOutlinePrimary}
                onClick={() => {
                  setModalShow(true);
                  setChamadoAtual(chamado);
                }}
              >
                {chamado.respostas.length > 0 ? "Analisar" : "Responder"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal - responder chamado (admin) */}
      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        centered
        size="lg"
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <strong>Responder Chamado</strong>
            <div className={styles.modalSubtitle}>
              <span>
                <strong>Chamado:</strong> {chamadoAtual?.num_Chamado || "N/A"}
              </span>
              <span>
                <strong>Criado em:</strong>{" "}
                {formatDate(chamadoAtual?.horariO_CRIADO)}
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className={styles.modalBody}>
          <div className={styles.fullWidthBlock}>
            <span className={styles.infoLabel}>Descrição do Chamado</span>
            <p className={styles.infoText}>{chamadoAtual?.descricao}</p>
          </div>

          <div className={styles.fullWidthBlock}>
            <span className={styles.infoLabel}>Resposta(s)</span>

            {chamadoAtual?.respostas && chamadoAtual.respostas.length > 0 ? (
              <div className={styles.responseHistory}>
                {chamadoAtual.respostas.map((resp) => (
                  <div key={resp.id} className={styles.responseItem}>
                    <p className={styles.responseText}>{resp.textO_RESPOSTA}</p>
                    <small className={styles.responseMeta}>
                      Respondido por: {resp.nomE_USUARIO_RESPOSTA} em{" "}
                      {formatDate(resp.datA_RESPOSTA)}
                    </small>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.errorText}>Nenhuma resposta encontrada.</p>
            )}
          </div>

          {chamadoAtual?.status !== "Encerrado" && (
            <div className={styles.fullWidthBlock}>
              <label htmlFor="resposta" className={styles.infoLabel}>
                Responda o Chamado
              </label>
              <textarea
                id="resposta"
                className={styles.textarea}
                value={resposta}
                onChange={(e) => setResposta(e.target.value)}
                rows={4}
                placeholder="Digite a resposta..."
              />
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <button
            className={styles.btnOutline}
            onClick={() => {
              setModalShow(false);
              setModalRelatoriosShow(true);
            }}
          >
            Relatórios
          </button>

          <button
            className={styles.btnSecondary}
            onClick={handleEncerrarChamado}
          >
            Encerrar Chamado
          </button>

          <button
            className={styles.btnPrimary}
            onClick={handleResponderChamado}
          >
            Responder
          </button>

          <button
            className={styles.btnDanger}
            onClick={() => setModalShow(false)}
          >
            Fechar
          </button>
        </Modal.Footer>
      </Modal>

      {/* Modal - relatórios */}
      <Modal
        show={modalRelatoriosShow}
        onHide={() => {
          setModalRelatoriosShow(false);
          setModalShow(true);
        }}
        centered
        size="lg"
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>Relatórios Enviados</Modal.Title>
        </Modal.Header>

        <Modal.Body className={styles.modalBody}>
          {chamadoAtual?.relatorios && chamadoAtual.relatorios.length > 0 ? (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Nome do Arquivo</th>
                    <th>Data de Envio</th>
                    <th>Enviado Por</th>
                  </tr>
                </thead>
                <tbody>
                  {chamadoAtual.relatorios.map((relatorio, index) => (
                    <tr key={index}>
                      <td>
                        <button
                          className={styles.linkButton}
                          onClick={() =>
                            handleDownload(
                              relatorio.caminho_arquivo,
                              relatorio.nome_arquivo,
                            )
                          }
                        >
                          {relatorio.nome_arquivo}
                        </button>
                      </td>
                      <td>{formatDate(relatorio.data_upload)}</td>
                      <td>{relatorio.enviador || "Desconhecido"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.errorText}>Nenhum relatório enviado</p>
          )}
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          {chamadoAtual && (
            <>
              <input
                type="file"
                id="inputRelatorio"
                className={styles.hiddenInput}
                onChange={(e) => handleUploadChamadoRelatorio(chamadoAtual, e)}
                accept=".pdf,.doc,.docx"
              />

              <button
                className={styles.btnPrimary}
                onClick={() =>
                  document.getElementById("inputRelatorio")?.click()
                }
              >
                {isLoading ? (
                  <Loading color="#fff" />
                ) : (
                  <>
                    <Upload size={16} /> Enviar Relatório
                  </>
                )}
              </button>
            </>
          )}
          <button
            className={styles.btnDanger}
            onClick={() => {
              setModalRelatoriosShow(false);
              setModalShow(true);
            }}
          >
            <X size={16} /> Fechar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Chamados;
